import React from "react"

import { useQuery } from "@tanstack/react-query"
import { DateTime } from "luxon"

import { Skeleton } from "@/components/ui/skeleton"
import { TableCell, TableRow } from "@/components/ui/table"
import type { KPIRequest } from "@/types/kpiTypes"
import { numberFmt } from "@/util/numberFmt"

import { getDigesterKPIs } from "../api/getDigesterKPIs"
import { getMetricColor } from "../util/getMetricColor"

import type { DigesterConfig, DigesterPod, MetricColorRange } from "../types/feedstockTypes"

export default function DigesterKPIs({
  groups,
  kpis,
  kpiTotals,
  colorRanges,
}: {
  groups: DigesterPod[]
  kpis: KPIRequest[]
  kpiTotals: ("Sum" | "Average")[]
  colorRanges: MetricColorRange[][]
}) {
  let colCount = 0
  groups.forEach((g) => g.digesters.forEach(() => colCount++))
  const rowCount = kpis.length
  const digesters: DigesterConfig[] = []
  groups.forEach((g) => g.digesters.forEach((d) => digesters.push(d)))

  const asOf = DateTime.now().setZone("America/Chicago").startOf("day").minus({ days: 1 })

  const { data } = useQuery({
    queryKey: ["digesterKPIs", { groups }],
    queryFn: () => getDigesterKPIs(asOf.toJSDate(), kpis),
  })

  return (
    <React.Fragment>
      <TableRow>
        <TableCell colSpan={colCount + 2} className="font-semibold text-lg px-4 pt-5">
          {asOf.toLocaleString(DateTime.DATE_HUGE)}
        </TableCell>
      </TableRow>
      {[...Array(rowCount).keys()].map((i) => {
        let total: number | undefined = data?.day
          .at(i)
          ?.values?.map((v) => v.value)
          .reduce((a, c) => (a || 0) + (Number.isFinite(c) ? c || 0 : 0))
        const count = data?.day.at(i)?.values?.map((v) => v.value).length
        if (kpiTotals[i] == "Average") {
          if (total !== undefined && count !== undefined) total = total / count
        }
        return (
          <TableRow key={i}>
            <TableCell className="p-4 text-muted-foreground font-semibold">
              {data ? (
                (
                  <>
                    {data.day.at(i)?.name} <i>({data.day.at(i)?.unit})</i>
                  </>
                ) || "N/A"
              ) : (
                <Skeleton className="w-full h-[15px]" />
              )}
            </TableCell>
            {digesters.map((d) => {
              const val = data
                ? data.day.at(i)?.values?.find((v) => v.dimensionKey == d.key)?.value
                : undefined
              const color = getMetricColor(colorRanges.at(i) || [], val || 0)
              return (
                <TableCell key={d.key} className="w-[80px] p-4">
                  {val !== undefined ? (
                    <span className={color}>{numberFmt(val)}</span>
                  ) : (
                    <Skeleton className="w-full h-[15px]" />
                  )}
                </TableCell>
              )
            })}
            <TableCell className="p-4 font-semibold">
              {total !== undefined ? numberFmt(total) : <Skeleton className="w-full h-[15px]" />}
            </TableCell>
          </TableRow>
        )
      })}
      <TableRow>
        <TableCell colSpan={colCount + 2} className="font-semibold text-lg px-4 pt-5">
          {asOf.toFormat("MMMM") + " MTD Averages"}
        </TableCell>
      </TableRow>
      {[...Array(rowCount).keys()].map((i) => {
        let total: number | undefined = data?.mtd
          .at(i)
          ?.values?.map((v) => v.value)
          .reduce((a, c) => (a || 0) + (Number.isFinite(c) ? c || 0 : 0))
        const count = data?.mtd.at(i)?.values?.map((v) => v.value).length
        if (kpiTotals[i] == "Average") {
          if (total !== undefined && count !== undefined) total = total / count
        }
        return (
          <TableRow key={i}>
            <TableCell className="p-4 text-muted-foreground font-semibold">
              {data ? (
                (
                  <>
                    {data.day.at(i)?.name} <i>({data.day.at(i)?.unit})</i>
                  </>
                ) || "N/A"
              ) : (
                <Skeleton className="w-full h-[15px]" />
              )}
            </TableCell>
            {digesters.map((d) => {
              const val = data
                ? data.mtd.at(i)?.values?.find((v) => v.dimensionKey == d.key)?.value
                : undefined
              const color = getMetricColor(colorRanges.at(i) || [], val || 0)
              return (
                <TableCell key={d.key} className="w-[80px] p-4">
                  {val !== undefined ? (
                    <span className={color}>{numberFmt(val)}</span>
                  ) : (
                    <Skeleton className="w-full h-[15px]" />
                  )}
                </TableCell>
              )
            })}
            <TableCell className="p-4 font-semibold">
              {total !== undefined ? numberFmt(total) : <Skeleton className="w-full h-[15px]" />}
            </TableCell>
          </TableRow>
        )
      })}
    </React.Fragment>
  )
}
